import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Check } from 'lucide-react';

const CreateTracker = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tags: [],
    customStatuses: [
      { text: 'Not Started', color: '#6B7280' },
      { text: 'In Progress', color: '#3B82F6' },
      { text: 'Completed', color: '#10B981' },
      { text: 'Delayed', color: '#EF4444' }
    ],
    items: [],
    collaborators: []
  });

  const [newTag, setNewTag] = useState({ text: '', color: '#3B82F6' });
  const [newStatus, setNewStatus] = useState({ text: '', color: '#3B82F6' });
  const [newItem, setNewItem] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log(formData);
    navigate('/tracker');
  };

  const addTag = () => {
    if (newTag.text.trim()) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag]
      });
      setNewTag({ text: '', color: '#3B82F6' });
    }
  };

  const addStatus = () => {
    if (newStatus.text.trim()) {
      setFormData({
        ...formData,
        customStatuses: [...formData.customStatuses, newStatus]
      });
      setNewStatus({ text: '', color: '#3B82F6' });
    }
  };

  const addItem = () => {
    if (newItem.trim()) {
      setFormData({
        ...formData,
        items: [...formData.items, { text: newItem, status: 'Not Started' }]
      });
      setNewItem('');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter tracker title"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Enter tracker description"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Tags */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Tags</h2>
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {formData.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-sm font-medium flex items-center"
                  style={{ 
                    backgroundColor: `${tag.color}15`,
                    color: tag.color
                  }}
                >
                  {tag.text}
                  <button
                    type="button"
                    onClick={() => {
                      const newTags = [...formData.tags];
                      newTags.splice(index, 1);
                      setFormData({ ...formData, tags: newTags });
                    }}
                    className="ml-2 hover:bg-white/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag.text}
                onChange={(e) => setNewTag({ ...newTag, text: e.target.value })}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Add a tag"
              />
              <input
                type="color"
                value={newTag.color}
                onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
                className="w-12 h-10 rounded-lg cursor-pointer"
              />
              <button
                type="button"
                onClick={addTag}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Custom Statuses */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Custom Statuses</h2>
          <div className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {formData.customStatuses.map((status, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-sm font-medium flex items-center"
                  style={{ 
                    backgroundColor: `${status.color}15`,
                    color: status.color
                  }}
                >
                  {status.text}
                  <button
                    type="button"
                    onClick={() => {
                      const newStatuses = [...formData.customStatuses];
                      newStatuses.splice(index, 1);
                      setFormData({ ...formData, customStatuses: newStatuses });
                    }}
                    className="ml-2 hover:bg-white/20 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newStatus.text}
                onChange={(e) => setNewStatus({ ...newStatus, text: e.target.value })}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Add a status"
              />
              <input
                type="color"
                value={newStatus.color}
                onChange={(e) => setNewStatus({ ...newStatus, color: e.target.value })}
                className="w-12 h-10 rounded-lg cursor-pointer"
              />
              <button
                type="button"
                onClick={addStatus}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Items List */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Items</h2>
          <div className="space-y-4">
            <div className="space-y-2">
              {formData.items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm text-gray-700">{item.text}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = [...formData.items];
                      newItems.splice(index, 1);
                      setFormData({ ...formData, items: newItems });
                    }}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="Add an item"
              />
              <button
                type="button"
                onClick={addItem}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/tracker')}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Check className="w-4 h-4 mr-2" />
            Create Tracker
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTracker; 